import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../hooks/useAuth'
import { subscribeUserFavorites, FavoriteProfessional } from '../lib/favoritesService'
import { getProfessionalById, Professional } from '../lib/professionalService'
import { getUserReviewSummary, ReviewSummary } from '../lib/reviewService'
import FavoriteButton from './FavoriteButton'
import ProfessionalProfileModal from './ProfessionalProfileModal'
import BookingCalendar from './BookingCalendar'

const SWE_SPECIALIZATIONS = [
  'All',
  'Frontend Engineer',
  'Backend Engineer',
  'Full Stack Engineer',
  'iOS Developer',
  'Android Developer',
  'Mobile Developer',
  'ML Engineer',
  'Data Engineer',
  'DevOps Engineer',
  'Security Engineer',
  'Platform Engineer',
  'Cloud Engineer',
]

const CHIP_LABELS: Record<string, string> = {
  'All': 'All',
  'Frontend Engineer': 'Frontend',
  'Backend Engineer': 'Backend',
  'Full Stack Engineer': 'Full-stack',
  'iOS Developer': 'iOS',
  'Android Developer': 'Android',
  'Mobile Developer': 'Mobile',
  'ML Engineer': 'ML',
  'Data Engineer': 'Data',
  'DevOps Engineer': 'DevOps',
  'Security Engineer': 'Security',
  'Platform Engineer': 'Platform',
  'Cloud Engineer': 'Cloud',
}

function getSpecLabel(specialization: string): string {
  return CHIP_LABELS[specialization] || specialization.replace(' Engineer', '').replace(' Developer', '')
}

function formatExperience(exp: string): string {
  if (!exp) return ''
  if (exp.includes('yr')) return exp
  const match = exp.match(/(\d+\+?(?:-\d+)?)\s*(?:years?|yrs?)/)
  if (match) {
    const num = match[1]
    return num.includes('+') || parseInt(num) === 1 ? `${num} yr` : `${num} yrs`
  }
  return exp
}

function formatSavedAgo(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffMin < 60) return 'Saved just now'
  if (diffHours < 24) return diffHours === 1 ? 'Saved 1 hour ago' : `Saved ${diffHours} hours ago`
  if (diffDays === 0) return 'Saved today'
  if (diffDays === 1) return 'Saved yesterday'
  if (diffDays < 7) return `Saved ${diffDays} days ago`
  if (diffWeeks === 1) return 'Saved last week'
  if (diffWeeks < 4) return `Saved ${diffWeeks} weeks ago`
  if (diffMonths === 1) return 'Saved last month'
  return `Saved ${diffMonths} months ago`
}

type SortOption = 'recent' | 'oldest' | 'name' | 'price-low' | 'price-high' | 'rating'

interface FavoritesProps {
  onNavigateToFindEngineers?: () => void
  onNavigateToCalendar?: () => void
}

export default function Favorites({ onNavigateToFindEngineers, onNavigateToCalendar }: FavoritesProps) {
  const { user } = useAuth()
  const [favorites, setFavorites] = useState<FavoriteProfessional[]>([])
  const [professionalsData, setProfessionalsData] = useState<Map<string, Professional>>(new Map())
  const [reviewSummaries, setReviewSummaries] = useState<Map<string, ReviewSummary>>(new Map())
  const [loading, setLoading] = useState(true)
  const [selectedProfessional, setSelectedProfessional] = useState<Professional | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showBookingCalendar, setShowBookingCalendar] = useState(false)
  const [bookingProfessional, setBookingProfessional] = useState<Professional | null>(null)
  const [selectedSpecialization, setSelectedSpecialization] = useState('All')
  const [sortBy, setSortBy] = useState<SortOption>('recent')

  // Subscribe to favorites in real-time
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false)
      return
    }

    setLoading(true)

    const unsubscribe = subscribeUserFavorites(user.uid, async (favs) => {
      setFavorites(favs)

      const dataMap = new Map<string, Professional>()
      const reviewMap = new Map<string, ReviewSummary>()

      await Promise.all(
        favs.map(async (fav) => {
          const prof = await getProfessionalById(fav.professionalId)
          if (prof) {
            dataMap.set(fav.professionalId, prof)
            const summary = await getUserReviewSummary(fav.professionalId)
            if (summary) {
              reviewMap.set(fav.professionalId, summary)
            }
          }
        })
      )

      setProfessionalsData(dataMap)
      setReviewSummaries(reviewMap)
      setLoading(false)
    })

    return unsubscribe
  }, [user?.uid])

  // Compute which specializations exist in favorites (for filter chips)
  const specCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    favorites.forEach(fav => {
      const prof = professionalsData.get(fav.professionalId)
      if (prof) {
        const spec = prof.specialization || prof.title || ''
        if (spec) counts[spec] = (counts[spec] || 0) + 1
      }
    })
    return counts
  }, [favorites, professionalsData])

  // Filter + sort favorites
  const filteredFavorites = useMemo(() => {
    let result = favorites.filter(fav => {
      const prof = professionalsData.get(fav.professionalId)
      if (!prof) return false
      if (selectedSpecialization === 'All') return true
      return prof.specialization === selectedSpecialization || prof.title === selectedSpecialization
    })

    // Sort
    result = [...result].sort((a, b) => {
      const profA = professionalsData.get(a.professionalId)
      const profB = professionalsData.get(b.professionalId)
      if (!profA || !profB) return 0

      switch (sortBy) {
        case 'recent':
          return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
        case 'oldest':
          return (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0)
        case 'name':
          return profA.displayName.localeCompare(profB.displayName)
        case 'price-low':
          return (profA.sessionRate || 0) - (profB.sessionRate || 0)
        case 'price-high':
          return (profB.sessionRate || 0) - (profA.sessionRate || 0)
        case 'rating': {
          const rA = reviewSummaries.get(a.professionalId)?.averageRating || 0
          const rB = reviewSummaries.get(b.professionalId)?.averageRating || 0
          return rB - rA
        }
        default:
          return 0
      }
    })

    return result
  }, [favorites, professionalsData, reviewSummaries, selectedSpecialization, sortBy])

  const handleProfileClick = async (favorite: FavoriteProfessional) => {
    const professional = professionalsData.get(favorite.professionalId)
    if (professional) {
      setSelectedProfessional(professional)
      setIsModalOpen(true)
    }
  }

  const handleBookClick = async (favorite: FavoriteProfessional) => {
    const professional = professionalsData.get(favorite.professionalId)
    if (professional) {
      setBookingProfessional(professional)
      setShowBookingCalendar(true)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedProfessional(null)
  }

  const handleCloseBooking = () => {
    setShowBookingCalendar(false)
    setBookingProfessional(null)
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/40"></div>
        <span className="ml-3 text-white/50 text-sm">Loading favorites...</span>
      </div>
    )
  }

  // Not signed in
  if (!user) {
    return (
      <div className="max-w-[1280px]">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-[44px] leading-[1.05] font-medium tracking-tight-1">
            Your <span className="font-serif italic font-normal">favorites</span>
          </h1>
        </header>
        <div className="text-center py-20">
          <svg className="mx-auto w-10 h-10 text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-4.5-9.5-9C.8 8.5 3 5 6.5 5c2 0 3.5 1 5.5 3.2C14 6 15.5 5 17.5 5 21 5 23.2 8.5 21.5 12 19 16.5 12 21 12 21z"/></svg>
          <h3 className="mt-3 text-sm font-medium text-white">Sign in required</h3>
          <p className="mt-1 text-sm text-white/50">Please sign in to view your saved engineers.</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (favorites.length === 0) {
    return (
      <div className="max-w-[1280px]">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-[44px] leading-[1.05] font-medium tracking-tight-1">
            Your <span className="font-serif italic font-normal">favorites</span>
          </h1>
          <p className="text-white/50 mt-2 text-sm max-w-2xl">
            Engineers you save will appear here for quick access.
          </p>
        </header>
        <div className="text-center py-20">
          <svg className="mx-auto w-10 h-10 text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-4.5-9.5-9C.8 8.5 3 5 6.5 5c2 0 3.5 1 5.5 3.2C14 6 15.5 5 17.5 5 21 5 23.2 8.5 21.5 12 19 16.5 12 21 12 21z"/></svg>
          <h3 className="mt-3 text-sm font-medium text-white">No favorites yet</h3>
          <p className="mt-1 text-sm text-white/50">
            Save engineers you're interested in to quickly find them later.
          </p>
          {onNavigateToFindEngineers && (
            <button onClick={onNavigateToFindEngineers} className="btn btn-outline mt-4">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2"/><circle cx="17" cy="9.5" r="2.4"/><path d="M3 19c0-3 2.7-5 6-5s6 2 6 5"/><path d="M14.5 19c0-2 2-3.5 4-3.5s3.5 1.5 3.5 3.5"/></svg>
              Find engineers
            </button>
          )}
        </div>
      </div>
    )
  }

  // Active specializations in favorites (for chips)
  const activeSpecs = SWE_SPECIALIZATIONS.filter(spec => {
    if (spec === 'All') return true
    return (specCounts[spec] || 0) > 0
  })

  return (
    <div className="max-w-[1280px]">

      {/* Header */}
      <header className="flex items-end justify-between gap-6 mb-7">
        <div>
          <h1 className="text-3xl sm:text-[44px] leading-[1.05] font-medium tracking-tight-1">
            Your <span className="font-serif italic font-normal">favorites</span>
          </h1>
          <p className="text-white/50 mt-2 text-[14px] max-w-2xl">
            {favorites.length} engineer{favorites.length !== 1 ? 's' : ''} you've saved for later. Tap the heart on any card to remove.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="select-bare"
          >
            <option value="recent">Recently saved</option>
            <option value="oldest">Saved oldest first</option>
            <option value="name">Name (A→Z)</option>
            <option value="price-low">Price (low → high)</option>
            <option value="price-high">Price (high → low)</option>
            <option value="rating">Highest rated</option>
          </select>
          {onNavigateToFindEngineers && (
            <button onClick={onNavigateToFindEngineers} className="btn btn-outline">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2"/><circle cx="17" cy="9.5" r="2.4"/><path d="M3 19c0-3 2.7-5 6-5s6 2 6 5"/><path d="M14.5 19c0-2 2-3.5 4-3.5s3.5 1.5 3.5 3.5"/></svg>
              Find more
            </button>
          )}
        </div>
      </header>

      {/* Specialization filter chips */}
      <div className="flex items-center gap-2 flex-wrap mb-6">
        {activeSpecs.map(spec => {
          const isActive = selectedSpecialization === spec
          const count = spec === 'All' ? favorites.length : (specCounts[spec] || 0)
          return (
            <button
              key={spec}
              onClick={() => setSelectedSpecialization(spec)}
              className={`filter-chip ${isActive ? 'filter-chip-active' : ''}`}
            >
              {CHIP_LABELS[spec] || spec}
              <span className="text-[11px] opacity-50 pl-1.5 ml-0.5" style={{ borderLeft: '1px solid currentColor' }}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Result count */}
      <div className="flex items-center justify-between mb-5">
        <div className="text-[12.5px] text-white/50">
          <span className="text-white/90 font-medium">{filteredFavorites.length}</span> saved engineer{filteredFavorites.length !== 1 ? 's' : ''}
          {selectedSpecialization !== 'All' && ` · ${CHIP_LABELS[selectedSpecialization] || selectedSpecialization}`}
        </div>
      </div>

      {/* Card grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredFavorites.map((favorite) => {
          const professional = professionalsData.get(favorite.professionalId)
          if (!professional) return null

          const review = reviewSummaries.get(favorite.professionalId)
          const hasReviews = review && review.totalReviews > 0
          const specLabel = getSpecLabel(professional.specialization || professional.title || '')
          const expLabel = formatExperience(professional.experience)
          const savedAgo = favorite.createdAt ? formatSavedAgo(favorite.createdAt.toDate()) : 'Saved'

          return (
            <article key={favorite.professionalId} className="eng-card">
              {/* Photo area */}
              <div className="eng-photo">
                <div className="dot-grid" style={{ position: 'absolute', inset: 0, opacity: 0.35 }}></div>

                {professional.profileImage ? (
                  <img
                    src={professional.profileImage}
                    alt={professional.displayName}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ zIndex: 1 }}
                  />
                ) : (
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 150" fill="none" stroke="currentColor" strokeWidth="1.4" style={{ color: 'hsl(0 0% 65% / 0.45)' }}>
                    <circle cx="100" cy="60" r="22"/>
                    <path d="M52 138c4-22 24-34 48-34s44 12 48 34"/>
                  </svg>
                )}

                {/* Rate badge */}
                <span className="badge-rate">
                  ${professional.sessionRate} <span className="opacity-70 font-medium ml-0.5">/ session</span>
                </span>

                {/* Heart / favorite button */}
                <div className="absolute top-3.5 right-3.5 z-10">
                  <FavoriteButton
                    professionalId={favorite.professionalId}
                    size="md"
                    className="!bg-black/40 !backdrop-blur-sm !shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] hover:!bg-black/60"
                  />
                </div>
              </div>

              {/* Card body */}
              <div className="flex-1 flex flex-col p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[16.5px] font-semibold tracking-tight-half truncate">{professional.displayName}</div>
                    <div className="text-white/50 text-[12.5px] mt-0.5 truncate">
                      {specLabel}{professional.company && <> · <span className="text-white/85">{professional.company}</span></>}
                    </div>
                  </div>
                  {expLabel && (
                    <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-white bg-white/[0.06]"
                      style={{ boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                      {expLabel}
                    </span>
                  )}
                </div>

                {/* Bio */}
                <p className="text-white/80 text-[13px] leading-relaxed mt-3 line-clamp-3">
                  {professional.bio}
                </p>

                {/* Meta row */}
                <div className="flex items-center gap-3 mt-4 text-[11.5px] text-white/50">
                  <span className="inline-flex items-center gap-1.5">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                    30 min
                  </span>
                  <span className="opacity-40">·</span>
                  {hasReviews ? (
                    <span className="inline-flex items-center gap-1">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" className="text-white/85"><path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/></svg>
                      {review.averageRating.toFixed(1)} · {review.totalReviews} review{review.totalReviews !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="italic">No reviews yet</span>
                  )}
                  <span className="opacity-40">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-4.5-9.5-9C.8 8.5 3 5 6.5 5c2 0 3.5 1 5.5 3.2C14 6 15.5 5 17.5 5 21 5 23.2 8.5 21.5 12 19 16.5 12 21 12 21z"/></svg>
                    {savedAgo}
                  </span>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 mt-5">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleBookClick(favorite) }}
                    className="btn btn-primary flex-1 justify-center"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M8 3v4M16 3v4M3.5 10h17"/></svg>
                    Book a session
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleProfileClick(favorite) }}
                    className="btn btn-outline !px-3.5"
                    aria-label="View profile"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                </div>
              </div>
            </article>
          )
        })}
      </section>

      {/* Empty filtered state */}
      {filteredFavorites.length === 0 && favorites.length > 0 && (
        <div className="text-center py-20">
          <svg className="mx-auto w-10 h-10 text-white/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2"/><circle cx="17" cy="9.5" r="2.4"/><path d="M3 19c0-3 2.7-5 6-5s6 2 6 5"/><path d="M14.5 19c0-2 2-3.5 4-3.5s3.5 1.5 3.5 3.5"/></svg>
          <h3 className="mt-3 text-sm font-medium text-white">No favorites in this category</h3>
          <p className="mt-1 text-sm text-white/50">
            Try a different specialization or browse all favorites.
          </p>
          <button
            onClick={() => setSelectedSpecialization('All')}
            className="btn btn-outline mt-4"
          >
            Show all favorites
          </button>
        </div>
      )}

      {/* Professional Profile Modal */}
      <ProfessionalProfileModal
        professional={selectedProfessional}
        open={isModalOpen}
        onClose={handleCloseModal}
        onBookSession={(prof) => {
          handleCloseModal()
          const favorite = favorites.find(f => f.professionalId === prof.uid)
          if (favorite) {
            handleBookClick(favorite)
          }
        }}
      />

      {/* Booking Calendar */}
      <BookingCalendar
        open={showBookingCalendar}
        onClose={handleCloseBooking}
        professional={bookingProfessional}
        onNavigateToCalendar={onNavigateToCalendar}
      />
    </div>
  )
}
